import React, { isValidElement, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  AiFillCheckCircle,
  AiFillCloseCircle,
  AiFillExclamationCircle,
  AiFillInfoCircle,
  AiOutlineClose,
  AiOutlineLoading,
} from 'react-icons/ai';

/*
 * Toast messages.
 *
 *   toast.success('Saved');
 *   toast.error('Failed', 5);                         // seconds
 *   toast.success({ content: 'Done', duration: 2, key: 'save' });
 *   const hide = toast.loading('Uploading…'); hide();  // loading stays until hidden
 *
 * Render <Toaster /> once near the root of the app.
 */

const ICONS = {
  success: AiFillCheckCircle,
  error: AiFillCloseCircle,
  warning: AiFillExclamationCircle,
  info: AiFillInfoCircle,
  loading: AiOutlineLoading,
};

const MAX_VISIBLE = 5;
let items = [];
let seq = 0;
const timers = new Map();
const listeners = new Set();

const emit = () => listeners.forEach(listener => listener(items));

function dismiss(key) {
  const item = items.find(t => t.key === key);
  clearTimeout(timers.get(key));
  timers.delete(key);
  if (!item) return;
  items = items.filter(t => t.key !== key);
  emit();
  item.onClose?.();
}

function show(type, content, duration, onClose) {
  const options =
    content && typeof content === 'object' && !isValidElement(content)
      ? content
      : { content, duration, onClose };

  const key = options.key ?? `toast-${(seq += 1)}`;
  const seconds = options.duration ?? (type === 'loading' ? 0 : 3);

  clearTimeout(timers.get(key));
  const entry = { key, type, content: options.content, onClose: options.onClose };
  const existing = items.findIndex(t => t.key === key);
  items =
    existing === -1
      ? [...items, entry].slice(-MAX_VISIBLE)
      : items.map(t => (t.key === key ? entry : t));
  emit();

  if (seconds > 0) timers.set(key, setTimeout(() => dismiss(key), seconds * 1000));

  const hide = () => dismiss(key);
  // Thenable, so callers can chain `.then` after the toast closes.
  hide.then = resolve => {
    const wait = seconds > 0 ? seconds * 1000 : 0;
    return new Promise(done => setTimeout(done, wait)).then(resolve);
  };
  return hide;
}

export const toast = {
  success: (content, duration, onClose) => show('success', content, duration, onClose),
  error: (content, duration, onClose) => show('error', content, duration, onClose),
  warning: (content, duration, onClose) => show('warning', content, duration, onClose),
  info: (content, duration, onClose) => show('info', content, duration, onClose),
  loading: (content, duration, onClose) => show('loading', content, duration, onClose),
  dismiss,
  destroy: () => {
    timers.forEach(clearTimeout);
    timers.clear();
    items = [];
    emit();
  },
};

export function Toaster() {
  const [list, setList] = useState(items);

  useEffect(() => {
    listeners.add(setList);
    setList(items);
    return () => listeners.delete(setList);
  }, []);

  if (typeof document === 'undefined') return null;

  return createPortal(
    <div className="toast-stack" role="status" aria-live="polite">
      {list.map(t => {
        const Icon = ICONS[t.type] || AiFillInfoCircle;
        return (
          <div key={t.key} className={`toast toast-${t.type}`}>
            <Icon className={`toast-icon${t.type === 'loading' ? ' icon-spin' : ''}`} />
            <span>{t.content}</span>
            <button
              type="button"
              className="toast-close"
              onClick={() => dismiss(t.key)}
              aria-label="Dismiss"
            >
              <AiOutlineClose />
            </button>
          </div>
        );
      })}
    </div>,
    document.body
  );
}

export default toast;
